import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import useCrowdGallery, { CrowdGallerySettings } from '@/src/GalleryConfiguration/components/hooks/useCrowdGallery';
import { Checkbox, Field, SectionCard, TextInput, Textarea } from '../../../components/forms';
import { CheckOpt, SectionStatus } from './parts';

/* Crowd gallery (guests text photos in) — its own save, like v2. */

interface CrowdForm {
    enabled: boolean;
    keyword: string;
    welcomeMessage: string;
    responseMessage: string;
    webResponseMessage: string;
    moderationEnabled: boolean;
    moderationNumber: string;
    sendPhoto: boolean;
    sendPersonalGalleryLink: boolean;
    sendGalleryLink: boolean;
}

const fromRow = (r: CrowdGallerySettings): CrowdForm => ({
    enabled: !!r.enabled,
    keyword: r.keyword ?? '',
    welcomeMessage: r.welcomeMessage ?? '',
    responseMessage: r.responseMessage ?? '',
    webResponseMessage: r.webResponseMessage ?? '',
    moderationEnabled: !!r.moderationEnabled,
    moderationNumber: r.moderationNumber ?? '',
    sendPhoto: !!r.sendPhoto,
    sendPersonalGalleryLink: !!r.sendPersonalGalleryLink,
    sendGalleryLink: !!r.sendGalleryLink,
});

const MESSAGES: Array<{ k: 'welcomeMessage' | 'responseMessage' | 'webResponseMessage'; l: string }> = [
    { k: 'welcomeMessage', l: 'Welcome message' },
    { k: 'responseMessage', l: 'Response message' },
    { k: 'webResponseMessage', l: 'Web response message' },
];

const REPLIES: Array<{ k: 'sendPhoto' | 'sendPersonalGalleryLink' | 'sendGalleryLink' | 'moderationEnabled'; l: string }> = [
    { k: 'sendPhoto', l: 'Send the photo back' },
    { k: 'sendPersonalGalleryLink', l: 'Send their personal gallery link' },
    { k: 'sendGalleryLink', l: 'Send the gallery link' },
    { k: 'moderationEnabled', l: 'Moderate texted photos' },
];

export default function CrowdSection({ galleryId, row }: { galleryId: number; row: CrowdGallerySettings | null }) {
    const { saveCrowdGallery } = useCrowdGallery();
    const [base, setBase] = useState<CrowdForm | null>(row ? fromRow(row) : null);
    const [form, setForm] = useState<CrowdForm | null>(base);
    const [state, setState] = useState<'idle' | 'saving' | 'failed' | 'saved'>('idle');

    useEffect(() => {
        const f = row ? fromRow(row) : null;
        setBase(f);
        setForm(f);
    }, [row]);

    const dirty = useMemo(() => !!form && JSON.stringify(form) !== JSON.stringify(base), [form, base]);
    const saving = state === 'saving';

    if (!row || !form) {
        return (
            <SectionCard title="Crowd gallery">
                <p className="quiet-empty">Crowd gallery isn’t set up for this gallery.</p>
            </SectionCard>
        );
    }

    const edit = <K extends keyof CrowdForm>(k: K, v: CrowdForm[K]) => {
        setForm((f) => (f ? { ...f, [k]: v } : f));
        if (state !== 'saving') setState('idle');
    };

    const save = async () => {
        if (!dirty || saving) return;
        setState('saving');
        const ok = await saveCrowdGallery(row.id, { ...form, galleryId }).catch(() => false);
        if (ok) {
            setBase(form);
            setState('saved');
        } else {
            setState('failed');
        }
    };

    return (
        <SectionCard title="Crowd gallery" action={<SectionStatus state={state} />}>
            <CheckOpt
                label="Guests text photos in"
                sub="An SMS keyword drops guest photos into the gallery."
                checked={form.enabled}
                disabled={saving}
                onChange={(on) => edit('enabled', on)}
            />
            {form.enabled && (
                <>
                    <Field label="Keyword">
                        <TextInput className="input ev-mono" value={form.keyword} disabled={saving} onChange={(e) => edit('keyword', e.target.value)} />
                    </Field>
                    {MESSAGES.map((m) => (
                        <label className="field wide ev-gap" key={m.k}>
                            <span className="field-l">{m.l}</span>
                            <Textarea rows={2} value={form[m.k]} disabled={saving} onChange={(e) => edit(m.k, e.target.value)} />
                        </label>
                    ))}
                    <div className="gl-checks ev-gap">
                        {REPLIES.map((c) => (
                            <Checkbox key={c.k} label={c.l} checked={form[c.k]} disabled={saving} onChange={(e) => edit(c.k, e.target.checked)} />
                        ))}
                    </div>
                </>
            )}
            {dirty && (
                <div className="gl-sec-save">
                    <button type="button" className="tb-btn ghost" disabled={saving} onClick={() => { setForm(base); setState('idle'); }}>
                        Discard
                    </button>
                    <button type="button" className="tb-new" disabled={saving} onClick={save}>
                        {saving ? 'Saving…' : 'Save crowd gallery'}
                    </button>
                </div>
            )}
        </SectionCard>
    );
}
